// Backend real da Central de Circularização (api/circularizacao) — CircularizacaoController.cs.
// Substitui mocks/mockCircularizacaoService.ts: mesmos nomes/formatos de função, para que nenhum
// componente precisasse mudar além da troca do import (conforme o próprio cabeçalho do mock
// previa). A diferença estrutural é o parâmetro de escopo: o mock usava empresaId (a empresa
// auditada); o Back-End real — como todo o resto do módulo de Auditoria (Laudo, Entrada de Dados
// etc.) — escopa por esteira/período ("periodo"), porque uma mesma empresa tem um processo de
// circularização por período de auditoria, não um único em comum entre todos os períodos.
//
// IDs: o Back-End usa long (number no JSON); o contrato de Front-End (ICircularizacao.tsx) usa
// string (herdado do mock, que gerava ids como "circ-...") — convertido aqui, nunca propagado aos
// componentes.
import { saveAs } from 'file-saver';
import api from '../../../../services/Api';
import {
  CartaStatus,
  CategoriaCircularizacao,
  IAlertaInconsistencia,
  ICartaCircularizacao,
  IContatoCircularizacao,
  IHistoricoAjusteCircularizacao,
  IIndicadoresCircularizacao,
  INovoRegistroManual,
  IPoliticaCobranca,
  IRegistroCircularizacao,
  IRespostaCircularizacao,
  ITemplateCircularizacao,
  PerfilTemplateCircularizacao,
  StatusValidacaoResposta,
  TipoTemplateCircularizacao,
} from '../interfaces/ICircularizacao';

const BASE = 'circularizacao';

interface IContatoDto {
  id: number;
  nomeCompleto: string;
  emailPrincipal: string;
  telefone: string;
  cargo?: string | null;
  departamento?: string | null;
  telefoneAlternativo?: string | null;
  emailAlternativo?: string | null;
  observacoes?: string | null;
  contatoPrincipal: boolean;
}

interface IRegistroDto {
  id: number;
  empresaAuditadaId: number;
  categoria: CategoriaCircularizacao;
  razaoSocialOriginal: string;
  razaoSocialExibicao: string;
  nomeFantasia?: string | null;
  cnpj: string;
  origemSelecao: IRegistroCircularizacao['origemSelecao'];
  justificativaOriginal: string;
  referenciaRegraSelecao?: string | null;
  situacaoRevisao: IRegistroCircularizacao['situacaoRevisao'];
  justificativaInclusaoManual?: string | null;
  justificativaExclusao?: string | null;
  justificativaAlteracao?: string | null;
  contatos: IContatoDto[];
  contatoResponsavelId?: number | null;
  dataPrevistaEnvio?: string | null;
  prazoResposta?: string | null;
  situacaoPreparacaoCarta: IRegistroCircularizacao['situacaoPreparacaoCarta'];
  excluida: boolean;
  alteradaPeloAuditor: boolean;
  criadoEm: string;
  atualizadoEm: string;
}

interface IHistoricoDto {
  id: number;
  registroId: number;
  empresaNome: string;
  categoria: CategoriaCircularizacao;
  operacao: IHistoricoAjusteCircularizacao['operacao'];
  informacaoOriginal?: string | null;
  informacaoAtualizada?: string | null;
  justificativa: string;
  responsavel: string;
  dataHora: string;
}

interface IAlertaDto {
  registroId: number;
  tipo: string;
  mensagem: string;
}

interface IOperacaoResultadoDto {
  sucesso: boolean;
  mensagem: string;
  aplicados: number;
}

interface ICartaDto {
  id: number;
  registroId: number;
  numeroCarta: string;
  status: CartaStatus;
  storageKey?: string | null;
  aprovadaEm?: string | null;
  criadoEm: string;
}

interface ITemplateVersaoDto {
  id: number;
  versao: number;
  assunto: string;
  corpoHtml: string;
  vigente: boolean;
}

interface ITemplateDto {
  id: number;
  categoria: CategoriaCircularizacao;
  tipo: TipoTemplateCircularizacao;
  perfil: PerfilTemplateCircularizacao;
  nome: string;
  escopo: ITemplateCircularizacao['escopo'];
  institucional: boolean;
  versaoVigente?: ITemplateVersaoDto | null;
}

interface IPoliticaDto {
  id: number;
  categoria?: CategoriaCircularizacao | null;
  ativa: boolean;
  diasLembretePreventivo: number;
  diasCobrancaAtraso: number;
  considerarDiasUteis: boolean;
  autorizadaEm?: string | null;
}

interface IRespostaDto {
  id: number;
  cartaId: number;
  remetenteEmail: string;
  assunto?: string | null;
  tipo: string;
  statusValidacao: StatusValidacaoResposta;
  recebidoEm: string;
}

const paraContato = (dto: IContatoDto): IContatoCircularizacao => ({
  id: String(dto.id),
  nomeCompleto: dto.nomeCompleto,
  emailPrincipal: dto.emailPrincipal,
  telefone: dto.telefone,
  cargo: dto.cargo ?? undefined,
  departamento: dto.departamento ?? undefined,
  telefoneAlternativo: dto.telefoneAlternativo ?? undefined,
  emailAlternativo: dto.emailAlternativo ?? undefined,
  observacoes: dto.observacoes ?? undefined,
  contatoPrincipal: dto.contatoPrincipal,
});

const paraRegistro = (dto: IRegistroDto): IRegistroCircularizacao => ({
  id: String(dto.id),
  empresaAuditadaId: dto.empresaAuditadaId,
  categoria: dto.categoria,
  razaoSocialOriginal: dto.razaoSocialOriginal,
  razaoSocialExibicao: dto.razaoSocialExibicao,
  nomeFantasia: dto.nomeFantasia ?? undefined,
  cnpj: dto.cnpj,
  origemSelecao: dto.origemSelecao,
  justificativaOriginal: dto.justificativaOriginal,
  referenciaRegraSelecao: dto.referenciaRegraSelecao ?? undefined,
  situacaoRevisao: dto.situacaoRevisao,
  justificativaInclusaoManual: dto.justificativaInclusaoManual ?? undefined,
  justificativaExclusao: dto.justificativaExclusao ?? undefined,
  justificativaAlteracao: dto.justificativaAlteracao ?? undefined,
  contatos: (dto.contatos ?? []).map(paraContato),
  contatoResponsavelId: dto.contatoResponsavelId != null ? String(dto.contatoResponsavelId) : undefined,
  dataPrevistaEnvio: dto.dataPrevistaEnvio ?? undefined,
  prazoResposta: dto.prazoResposta ?? undefined,
  situacaoPreparacaoCarta: dto.situacaoPreparacaoCarta,
  excluida: dto.excluida,
  alteradaPeloAuditor: dto.alteradaPeloAuditor,
  criadoEm: dto.criadoEm,
  atualizadoEm: dto.atualizadoEm,
});

const paraHistorico = (dto: IHistoricoDto): IHistoricoAjusteCircularizacao => ({
  id: String(dto.id),
  registroId: String(dto.registroId),
  empresaNome: dto.empresaNome,
  categoria: dto.categoria,
  operacao: dto.operacao,
  informacaoOriginal: dto.informacaoOriginal ?? undefined,
  informacaoAtualizada: dto.informacaoAtualizada ?? undefined,
  justificativa: dto.justificativa,
  responsavel: dto.responsavel,
  dataHora: dto.dataHora,
});

const paraAlerta = (dto: IAlertaDto): IAlertaInconsistencia => ({
  registroId: String(dto.registroId),
  tipo: dto.tipo,
  mensagem: dto.mensagem,
});

const paraCarta = (dto: ICartaDto): ICartaCircularizacao => ({
  id: String(dto.id),
  registroId: String(dto.registroId),
  numeroCarta: dto.numeroCarta,
  status: dto.status,
  storageKey: dto.storageKey ?? undefined,
  aprovadaEm: dto.aprovadaEm ?? undefined,
  criadoEm: dto.criadoEm,
});

const paraTemplate = (dto: ITemplateDto): ITemplateCircularizacao => ({
  id: String(dto.id),
  categoria: dto.categoria,
  tipo: dto.tipo,
  perfil: dto.perfil,
  nome: dto.nome,
  escopo: dto.escopo,
  institucional: dto.institucional,
  versaoVigente: dto.versaoVigente
    ? {
        id: String(dto.versaoVigente.id),
        versao: dto.versaoVigente.versao,
        assunto: dto.versaoVigente.assunto,
        corpoHtml: dto.versaoVigente.corpoHtml,
        vigente: dto.versaoVigente.vigente,
      }
    : undefined,
});

const paraPolitica = (dto: IPoliticaDto): IPoliticaCobranca => ({
  id: String(dto.id),
  categoria: dto.categoria ?? undefined,
  ativa: dto.ativa,
  diasLembretePreventivo: dto.diasLembretePreventivo,
  diasCobrancaAtraso: dto.diasCobrancaAtraso,
  considerarDiasUteis: dto.considerarDiasUteis,
  autorizadaEm: dto.autorizadaEm ?? undefined,
});

const paraResposta = (dto: IRespostaDto): IRespostaCircularizacao => ({
  id: String(dto.id),
  cartaId: String(dto.cartaId),
  remetenteEmail: dto.remetenteEmail,
  assunto: dto.assunto ?? undefined,
  tipo: dto.tipo,
  statusValidacao: dto.statusValidacao,
  recebidoEm: dto.recebidoEm,
});

export const CircularizacaoService = {
  async listarAtivos(esteiraId: number, categoria?: CategoriaCircularizacao): Promise<IRegistroCircularizacao[]> {
    const { data } = await api.get<IRegistroDto[]>(`${BASE}/registros`, { params: { periodo: esteiraId, categoria } });
    return (data || []).map(paraRegistro);
  },

  async listarExcluidos(esteiraId: number, categoria?: CategoriaCircularizacao): Promise<IRegistroCircularizacao[]> {
    const { data } = await api.get<IRegistroDto[]>(`${BASE}/registros/excluidos`, { params: { periodo: esteiraId, categoria } });
    return (data || []).map(paraRegistro);
  },

  // Não existe endpoint dedicado "obter 1 registro" no Back-End — reaproveita a listagem (já
  // paginada por categoria) e busca pelo id, evitando criar uma rota só para isto.
  async obterRegistro(esteiraId: number, categoria: CategoriaCircularizacao, registroId: string): Promise<IRegistroCircularizacao | undefined> {
    const [ativos, excluidos] = await Promise.all([
      CircularizacaoService.listarAtivos(esteiraId, categoria),
      CircularizacaoService.listarExcluidos(esteiraId, categoria),
    ]);
    return [...ativos, ...excluidos].find((r) => r.id === registroId);
  },

  async incluirEmpresa(esteiraId: number, dados: INovoRegistroManual): Promise<IRegistroCircularizacao> {
    const { data } = await api.post<IRegistroDto>(`${BASE}/registros`, dados, { params: { periodo: esteiraId } });
    return paraRegistro(data);
  },

  async excluirEmpresa(esteiraId: number, registroId: string, justificativa: string): Promise<{ sucesso: boolean; mensagem: string }> {
    const { data } = await api.post<IOperacaoResultadoDto>(`${BASE}/registro/${registroId}/excluir`, { justificativa });
    return { sucesso: data.sucesso, mensagem: data.mensagem };
  },

  async restaurarEmpresa(esteiraId: number, registroId: string): Promise<{ sucesso: boolean; mensagem: string }> {
    const { data } = await api.post<IOperacaoResultadoDto>(`${BASE}/registro/${registroId}/restaurar`);
    return { sucesso: data.sucesso, mensagem: data.mensagem };
  },

  async alterarJustificativaExclusao(esteiraId: number, registroId: string, novaJustificativa: string): Promise<void> {
    await api.put(`${BASE}/registro/${registroId}/justificativa-exclusao`, { justificativa: novaJustificativa });
  },

  async editarEmpresa(
    esteiraId: number,
    registroId: string,
    dados: { razaoSocialExibicao?: string; nomeFantasia?: string; cnpj?: string; categoria?: CategoriaCircularizacao },
    justificativa: string
  ): Promise<{ sucesso: boolean; mensagem: string }> {
    const { data } = await api.put<IOperacaoResultadoDto>(`${BASE}/registro/${registroId}`, { ...dados, justificativa });
    return { sucesso: data.sucesso, mensagem: data.mensagem };
  },

  async salvarContato(
    esteiraId: number,
    registroId: string,
    contatoDados: Omit<IContatoCircularizacao, 'id'> & { id?: string }
  ): Promise<IContatoCircularizacao> {
    const { data } = await api.post<IContatoDto>(`${BASE}/registro/${registroId}/contato`, {
      ...contatoDados,
      id: contatoDados.id ? Number(contatoDados.id) : undefined,
    });
    return paraContato(data);
  },

  async excluirContato(esteiraId: number, registroId: string, contatoId: string): Promise<void> {
    await api.delete(`${BASE}/registro/${registroId}/contato/${contatoId}`);
  },

  async definirContatoPrincipal(esteiraId: number, registroId: string, contatoId: string): Promise<void> {
    await api.post(`${BASE}/registro/${registroId}/contato/${contatoId}/principal`);
  },

  async definirPlanejamento(
    esteiraId: number,
    registroId: string,
    dados: { dataPrevistaEnvio?: string; prazoResposta?: string; contatoResponsavelId?: string }
  ): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const { data } = await api.put<IOperacaoResultadoDto>(`${BASE}/registro/${registroId}/planejamento`, {
        dataPrevistaEnvio: dados.dataPrevistaEnvio,
        prazoResposta: dados.prazoResposta,
        contatoResponsavelId: dados.contatoResponsavelId ? Number(dados.contatoResponsavelId) : undefined,
      });
      return { sucesso: data.sucesso, mensagem: data.mensagem };
    } catch (err: any) {
      return { sucesso: false, mensagem: err?.response?.data?.mensagem || 'Não foi possível atualizar o planejamento.' };
    }
  },

  async aplicarPrazoEmLote(
    esteiraId: number,
    registroIds: string[],
    dados: { dataPrevistaEnvio?: string; prazoResposta?: string }
  ): Promise<{ sucesso: boolean; mensagem: string; aplicados: number }> {
    try {
      const { data } = await api.post<IOperacaoResultadoDto>(
        `${BASE}/registros/prazo-lote`,
        { registroIds: registroIds.map(Number), ...dados },
        { params: { periodo: esteiraId } }
      );
      return data;
    } catch (err: any) {
      return { sucesso: false, mensagem: err?.response?.data?.mensagem || 'Não foi possível aplicar o prazo em lote.', aplicados: 0 };
    }
  },

  async prepararCarta(esteiraId: number, registroId: string): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const { data } = await api.post<IOperacaoResultadoDto>(`${BASE}/registro/${registroId}/carta/preparar`, {});
      return { sucesso: data.sucesso, mensagem: data.mensagem };
    } catch (err: any) {
      return { sucesso: false, mensagem: err?.response?.data?.mensagem || 'Não foi possível preparar a carta.' };
    }
  },

  async marcarCategoriaNaoAplicavel(esteiraId: number, categoria: CategoriaCircularizacao, justificativa: string): Promise<void> {
    await api.post(`${BASE}/categoria/${categoria}/nao-aplicavel`, { justificativa }, { params: { periodo: esteiraId } });
  },

  async obterJustificativaNaoAplicavel(esteiraId: number, categoria: CategoriaCircularizacao): Promise<string | undefined> {
    const { data } = await api.get<{ justificativa?: string }>(`${BASE}/categoria/${categoria}/nao-aplicavel/justificativa`, {
      params: { periodo: esteiraId },
    });
    return data?.justificativa ?? undefined;
  },

  async reativarCategoria(esteiraId: number, categoria: CategoriaCircularizacao): Promise<void> {
    await api.delete(`${BASE}/categoria/${categoria}/nao-aplicavel`, { params: { periodo: esteiraId } });
  },

  async obterIndicadores(esteiraId: number, categoria?: CategoriaCircularizacao): Promise<IIndicadoresCircularizacao> {
    const { data } = await api.get<IIndicadoresCircularizacao>(`${BASE}/indicadores`, { params: { periodo: esteiraId, categoria } });
    return data;
  },

  async listarCategoriasComPrazoVencido(esteiraId: number): Promise<{ categoria: CategoriaCircularizacao; quantidade: number }[]> {
    const { data } = await api.get<{ categoria: CategoriaCircularizacao; quantidade: number }[]>(`${BASE}/categorias-prazo-vencido`, {
      params: { periodo: esteiraId },
    });
    return data || [];
  },

  async listarHistorico(esteiraId: number, categoria?: CategoriaCircularizacao): Promise<IHistoricoAjusteCircularizacao[]> {
    const { data } = await api.get<IHistoricoDto[]>(`${BASE}/historico`, { params: { periodo: esteiraId, categoria } });
    return (data || []).map(paraHistorico);
  },

  async listarAlertasInconsistencia(esteiraId: number, categoria?: CategoriaCircularizacao): Promise<IAlertaInconsistencia[]> {
    const { data } = await api.get<IAlertaDto[]>(`${BASE}/alertas`, { params: { periodo: esteiraId, categoria } });
    return (data || []).map(paraAlerta);
  },

  // --- Carta: status completo, aprovação, conclusão, download (Cards 08/09/12) ---

  async obterCarta(registroId: string): Promise<ICartaCircularizacao | undefined> {
    const { data } = await api.get<ICartaDto | null>(`${BASE}/registro/${registroId}/carta`);
    return data ? paraCarta(data) : undefined;
  },

  async aprovarCarta(cartaId: string, autorizarEnvioInicial: boolean): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const { data } = await api.post<IOperacaoResultadoDto>(`${BASE}/carta/${cartaId}/aprovar`, { autorizarEnvioInicial });
      return { sucesso: data.sucesso, mensagem: data.mensagem };
    } catch (err: any) {
      return { sucesso: false, mensagem: err?.response?.data?.mensagem || 'Não foi possível aprovar a carta.' };
    }
  },

  async concluirCarta(cartaId: string, justificativa: string): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const { data } = await api.post<IOperacaoResultadoDto>(`${BASE}/carta/${cartaId}/concluir`, { justificativa });
      return { sucesso: data.sucesso, mensagem: data.mensagem };
    } catch (err: any) {
      return { sucesso: false, mensagem: err?.response?.data?.mensagem || 'Não foi possível concluir o procedimento.' };
    }
  },

  async baixarDocumentoCarta(cartaId: string, numeroCarta: string): Promise<void> {
    const response = await api.get(`${BASE}/carta/${cartaId}/download`, { responseType: 'blob' });
    saveAs(new Blob([response.data], { type: 'application/pdf' }), `${numeroCarta}.pdf`);
  },

  // --- Templates institucionais/personalizados (Card 07) ---

  async listarTemplates(categoria: CategoriaCircularizacao, tipo: TipoTemplateCircularizacao): Promise<ITemplateCircularizacao[]> {
    const { data } = await api.get<ITemplateDto[]>(`${BASE}/templates`, { params: { categoria, tipo } });
    return (data || []).map(paraTemplate);
  },

  async duplicarTemplate(templateOrigemId: string, nome: string): Promise<ITemplateCircularizacao> {
    const { data } = await api.post<ITemplateDto>(`${BASE}/templates/duplicar`, {
      templateOrigemId: Number(templateOrigemId),
      nome,
      escopo: 'PESSOAL',
    });
    return paraTemplate(data);
  },

  async atualizarTemplate(templateId: string, assunto: string, corpoHtml: string): Promise<ITemplateCircularizacao> {
    const { data } = await api.put<ITemplateDto>(`${BASE}/templates/${templateId}`, {
      // O Back-End reaproveita o DTO de "salvar" para a atualização de versão — categoria/tipo/
      // perfil/nome/escopo são ignorados nesta rota (só assunto/corpoHtml importam).
      categoria: '', tipo: '', perfil: '', nome: '', assunto, corpoHtml,
    });
    return paraTemplate(data);
  },

  async semearTemplatesInstitucionais(): Promise<number> {
    const { data } = await api.post<{ criados: number }>(`${BASE}/templates/semear-institucionais`);
    return data?.criados ?? 0;
  },

  // --- Política de cobrança automática (Cards 09/10) ---

  async definirPoliticaCobranca(
    esteiraId: number,
    dados: { categoria?: CategoriaCircularizacao; ativa: boolean; diasLembretePreventivo: number; diasCobrancaAtraso: number; considerarDiasUteis: boolean }
  ): Promise<IPoliticaCobranca> {
    const { data } = await api.post<IPoliticaDto>(`${BASE}/politica-cobranca`, dados, { params: { periodo: esteiraId } });
    return paraPolitica(data);
  },

  async listarPoliticasCobranca(esteiraId: number): Promise<IPoliticaCobranca[]> {
    const { data } = await api.get<IPoliticaDto[]>(`${BASE}/politicas-cobranca`, { params: { periodo: esteiraId } });
    return (data || []).map(paraPolitica);
  },

  // --- Respostas recebidas e classificação (Cards 11/12) ---

  async listarRespostasPendentes(esteiraId: number): Promise<IRespostaCircularizacao[]> {
    const { data } = await api.get<IRespostaDto[]>(`${BASE}/respostas/pendentes`, { params: { periodo: esteiraId } });
    return (data || []).map(paraResposta);
  },

  async classificarResposta(
    respostaId: string,
    statusValidacao: StatusValidacaoResposta,
    justificativa: string
  ): Promise<{ sucesso: boolean; mensagem: string }> {
    try {
      const { data } = await api.post<IOperacaoResultadoDto>(`${BASE}/respostas/${respostaId}/classificar`, {
        statusValidacao,
        justificativa,
      });
      return { sucesso: data.sucesso, mensagem: data.mensagem };
    } catch (err: any) {
      return { sucesso: false, mensagem: err?.response?.data?.mensagem || 'Não foi possível classificar a resposta.' };
    }
  },
};
